// Katarina Death Lotus VFX:
//   KatarinaDashVFX        — purple particle-trail dash to landing position
//   KatarinaHighlightVFX   — glowing hex outlines on nearby enemies after landing
//   KatarinaDaggerThrowVFX — dagger projectile from landing pos to each target
//   KatarinaIconSpinVFX    — rotates the caster icon while daggers are thrown

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";
import { KAT_WINDUP_MS, KAT_STAGGER_MS, KAT_TRAVEL_MS } from "../../units/abilities/KatarinaDeathLotus";

// ── Dash VFX ─────────────────────────────────────────────────────────────────

const DASH_WINDUP_MS = 80;
const DASH_TRAVEL_MS = 220;
const DASH_LAND_MS   = 200;
const DASH_TOTAL     = DASH_WINDUP_MS + DASH_TRAVEL_MS + DASH_LAND_MS; // = 500, matches KAT_DASH_TOTAL_MS

type TrailSegment = { x: number; y: number; age: number };

export class KatarinaDashVFX implements Animation {
  private elapsed = 0;
  private trail: TrailSegment[] = [];
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private endPx: Point,
    private hexSize: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, DASH_TOTAL);

    const dashT = Math.max(0, (this.elapsed - DASH_WINDUP_MS) / DASH_TRAVEL_MS);
    if (dashT > 0 && dashT <= 1) {
      const eased = Easing.easeInOutCubic(Math.min(dashT, 1));
      this.visual.x = this.startPx.x + (this.endPx.x - this.startPx.x) * eased;
      this.visual.y = this.startPx.y + (this.endPx.y - this.startPx.y) * eased;
      if (Math.random() < 0.6) {
        this.trail.push({
          x: this.visual.x + (Math.random() - 0.5) * 8,
          y: this.visual.y + (Math.random() - 0.5) * 8,
          age: 0,
        });
      }
    }

    for (const seg of this.trail) seg.age += dt;
    this.trail = this.trail.filter((s) => s.age < 220);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.shadowColor = "#d08aff";
    ctx.shadowBlur = 14;
    for (const seg of this.trail) {
      const lifeT = seg.age / 220;
      ctx.globalAlpha = (1 - lifeT) * 0.7;
      const r = 4 * (1 - lifeT);
      ctx.fillStyle = lifeT < 0.4 ? "#a855f7" : "#7c3aed";
      ctx.beginPath();
      ctx.arc(seg.x, seg.y, Math.max(r, 0.5), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const landStart = DASH_WINDUP_MS + DASH_TRAVEL_MS;
    if (this.elapsed >= landStart) {
      const landT = (this.elapsed - landStart) / DASH_LAND_MS;
      const outerR = this.hexSize * 1.6 * Easing.easeOutCubic(landT);
      const alpha = 1 - landT;
      ctx.save();
      ctx.globalAlpha = alpha * 0.8;
      ctx.strokeStyle = "#a855f7";
      ctx.lineWidth = 3 * (1 - landT) + 1;
      ctx.shadowColor = "#d08aff";
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.arc(this.endPx.x, this.endPx.y, outerR, 0, Math.PI * 2);
      ctx.stroke();

      const flashR = this.hexSize * 0.5 * (1 - Easing.easeInQuad(landT));
      if (flashR > 0) {
        ctx.globalAlpha = alpha * 0.9;
        ctx.fillStyle = "#ffffff";
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(this.endPx.x, this.endPx.y, flashR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.elapsed >= DASH_TOTAL;
  }
}

// ── Hex highlight (windup after landing) ─────────────────────────────────────

function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = Math.PI * (-0.5 + i / 3);
    const x = cx + size * Math.cos(angle);
    const y = cy + size * Math.sin(angle);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export class KatarinaHighlightVFX implements Animation {
  private elapsed = 0;
  onEnd?: () => void;

  constructor(
    private targets: Point[],
    private hexSize: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, KAT_WINDUP_MS);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed / KAT_WINDUP_MS;
    const fadeIn  = Math.min(t / 0.25, 1);
    const fadeOut = t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
    const alpha = fadeIn * fadeOut;
    const pulse = 0.8 + 0.2 * Math.sin(this.elapsed / 70);

    for (const tgt of this.targets) {
      ctx.save();
      ctx.globalAlpha = alpha * 0.18;
      ctx.fillStyle = "#e040ff";
      hexPath(ctx, tgt.x, tgt.y, this.hexSize * 0.88);
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = alpha * 0.95;
      ctx.strokeStyle = "#e040ff";
      ctx.lineWidth = 2.5;
      ctx.shadowColor = "#e040ff";
      ctx.shadowBlur = 18 * pulse;
      hexPath(ctx, tgt.x, tgt.y, this.hexSize * 0.88);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = alpha * 0.9;
      ctx.fillStyle = "#ff80ff";
      ctx.shadowColor = "#ff80ff";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(tgt.x, tgt.y, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.elapsed >= KAT_WINDUP_MS;
  }
}

// ── Dagger throw (one per target, staggered) ─────────────────────────────────

export class KatarinaDaggerThrowVFX implements Animation {
  private elapsed = 0;
  private fired = false;
  onEnd?: () => void;
  private readonly totalMs: number;

  constructor(
    private from: Point,
    private to: Point,
    private startDelay: number,
    private onLand?: () => void,
  ) {
    this.totalMs = startDelay + KAT_TRAVEL_MS;
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.totalMs);
    if (!this.fired && this.elapsed >= this.totalMs) {
      this.fired = true;
      this.onLand?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed <= this.startDelay) return;
    const t = (this.elapsed - this.startDelay) / KAT_TRAVEL_MS;
    if (t >= 1) return;

    const eased = Easing.easeInOutCubic(t);
    const x = this.from.x + (this.to.x - this.from.x) * eased;
    const y = this.from.y + (this.to.y - this.from.y) * eased;
    const angle = Math.atan2(this.to.y - this.from.y, this.to.x - this.from.x);

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Trail
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = "#9c27b0";
    ctx.shadowColor = "#c850f0";
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(-8, 0);
    ctx.lineTo(-26, -2.5);
    ctx.lineTo(-26, 2.5);
    ctx.closePath();
    ctx.fill();

    // Blade
    ctx.globalAlpha = 1;
    ctx.shadowColor = "#e040ff";
    ctx.shadowBlur = 16;
    ctx.fillStyle = "#c850f0";
    ctx.beginPath();
    ctx.moveTo(15, 0);
    ctx.lineTo(-6, -3.5);
    ctx.lineTo(-6, 3.5);
    ctx.closePath();
    ctx.fill();

    // Bright center edge
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#f0b0ff";
    ctx.beginPath();
    ctx.moveTo(12, 0);
    ctx.lineTo(-4, -1.5);
    ctx.lineTo(-4, 1.5);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.totalMs;
  }
}

// ── Icon spin ─────────────────────────────────────────────────────────────────

export class KatarinaIconSpinVFX implements Animation {
  private elapsed = 0;
  onEnd?: () => void;
  private readonly totalMs: number;

  constructor(
    private visual: UnitVisual,
    private windup: number,
    private spinDuration: number,
  ) {
    this.totalMs = windup + spinDuration;
  }

  update(dt: number): void {
    if (this.visual.dead) { this.visual.rotation = 0; return; }
    this.elapsed = Math.min(this.elapsed + dt, this.totalMs);

    if (this.elapsed >= this.windup) {
      const t = Math.min((this.elapsed - this.windup) / this.spinDuration, 1);
      if (t < 1) {
        const eased = t < 0.5
          ? Easing.easeInCubic(t / 0.5)
          : Easing.easeOutCubic((t - 0.5) / 0.5);
        this.visual.rotation = eased * Math.PI * 5; // ~2.5 full rotations
      } else {
        this.visual.rotation = 0;
      }
    }
  }

  draw(_ctx: CanvasRenderingContext2D): void {}

  isDone(): boolean {
    return this.visual.dead || this.elapsed >= this.totalMs;
  }
}
